import { IPaginatable, httpGet, httpPutEmpty, httpPostEmpty, httpDeleteEmpty } from './HttpService';

export enum CommissionType {
    Self = 1,
    Basic,
    Simple,
    Advance,
}

export const getCommissionTypeName = (type: CommissionType): string => {
    switch (type) {
        case CommissionType.Self:
            return "직접출원";
        case CommissionType.Basic:
            return "바로출원";
        case CommissionType.Simple:
            return "간편출원";
        case CommissionType.Advance:
            return "심화출원";
    }
};

export enum OrderItemStep {
    Pending = 1,
    Request,
    Procedure,
    Screening,
    Report,
    Complete
};

export const getOrderItemStepName = (step: OrderItemStep): string => {
    switch (step) {
        case OrderItemStep.Pending:
            return "대기";
        case OrderItemStep.Request:
            return "출원의뢰";
        case OrderItemStep.Procedure:
            return "출원진행";
        case OrderItemStep.Screening:
            return "심사중";
        case OrderItemStep.Report:
            return "심사결과발표";
        case OrderItemStep.Complete:
            return "등록완료";
    }
};

export enum OrderPaymentStatus {
    Pending = 1,
    WaitingForDeposit,
    Done,
    Canceled
}

export const getOrderPaymentStatusName = (status: OrderPaymentStatus): string => {
    switch (status) {
        case OrderPaymentStatus.Pending:
            return "결제 대기";
        case OrderPaymentStatus.WaitingForDeposit:
            return "입금 대기";
        case OrderPaymentStatus.Done:
            return "완료";
        case OrderPaymentStatus.Canceled:
            return "취소";
    }
}

interface IOperatorInfo {
    fullName: string;
    email: string;
    phoneNumber: string;
    telephoneNumber?: string;
}

interface IApplicantInfo {
    koreanFullName: string;
    englishFullName: string;
    email: string;
    phoneNumber: string;
    applicantCode: string;
}

export interface IOrderItemStepHistory {
    ids: Array<string>;
    utcCreatedDate: string;
    step: OrderItemStep;
    description?: string;
}

export interface IOrderRow {
    id: string;
    publicNumber: number;
    UtcCreatedDate: string;
    userId: string;
    userName: string;
    fullName: string;
    totalAmountWIthVAT: number;
    koreanTrademarkName: string;
    englishTrademarkName: string;
    paymentStatus: OrderPaymentStatus;
    applicant: IApplicantInfo;
    operator: IOperatorInfo;
}

export interface IGoodsService {
    classificationCode: string;
    koreanText: string;
    englishText: string;
    similarCodes: string;
}

export interface IProductCategory {
    c1: string;
    c2: string;
    c3: string;
    c4: string;
}

export interface IAttachment {
    id: string;
    fileName: string;
    contentType: string;
}

export interface IOrderItem {
    id: string;
    category: string;
    classificationCode: string;
    classificationDescription: string;
    checkPoint?: number;
    commissionType: CommissionType;
    commission: number;
    isQuick: boolean;
    quickCommission: number;
    officialFee: number;
    officeFee: number;
    amount: number;
    currentStep: OrderItemStep;
}

export interface IOrderItemGroup {
    commissionType: CommissionType;
    isQuick: boolean;
    currentStep: OrderItemStep;
    items: Array<IOrderItem>;
}

export interface IOrderItemGroupV2 {
    commissionType: CommissionType;
    isQuick: boolean;
    currentStep: OrderItemStep;
    ids: Array<string>;
    classifications: Array<{ code: string, description: string }>;
    stepHistories: Array<IOrderItemStepHistory>;
}

export interface IOrderDetail {
    id: string;
    publicNumber: number;
    utcCreatedDate: string;
    userId: string;
    userName: string;
    fullName: string;
    applicant: IApplicantInfo;
    operator: IOperatorInfo;
    koreanTrademarkName: string;
    englishTrademarkName: string;
    description: string;
    totalAmountWIthVAT: number;
    paymentStatus: OrderPaymentStatus;
    stampId?: string;
    recommendation?: string;
    goodsServices?: Array<IGoodsService>;
    productCategories?: Array<IProductCategory>;
    attachments?: Array<IAttachment>;
    itemGroups: Array<IOrderItemGroup>;
    groups: Array<IOrderItemGroupV2>;
}

export interface IOrderSearchBindingModel {
    koreanTrademarkName?: string;
    englishTrademarkName?: string;
    applicantCode?: string;
    user?: string;
    orderOperator?: string;
    applicant?: string;
    recommendation?: string;
    pageIndex?: number;
    pageSize?: number;
}

export const getDefaultOrderSearchBindingModel = (): IOrderSearchBindingModel => {
    return {
        koreanTrademarkName: undefined,
        englishTrademarkName: undefined,
        applicantCode: undefined,
        user: undefined,
        orderOperator: undefined,
        applicant: undefined,
        recommendation: undefined,
        pageIndex: 0,
        pageSize: 10
    };
}

export const getOrders = async (model: IOrderSearchBindingModel) => {
    const result = await httpGet<IPaginatable<IOrderRow>>("/orders", model);

    return result;
}

export const getOrderDetails = async (orderId: string) => {
    const result = await httpGet<IOrderDetail>(`/orders/${orderId}`);

    return result;
}

export const deleteOrder = async (orderId: string) => {
    const result = await httpDeleteEmpty(`/orders/${orderId}`);

    return result;
}

export const postHistory = async (orderId: string, itemIds: Array<string>, step: OrderItemStep, description: string) => {
    const result = await httpPostEmpty(`/orders/${orderId}/item-step-histories`, {
        itemIds: itemIds,
        step: step,
        description: description
    });

    return result;
}

export const putHistory = async (orderId: string, historyIds: Array<string>, description: string | undefined) => {
    const result = await httpPutEmpty(`/orders/${orderId}/item-step-histories/edit`, {
        historyIds: historyIds,
        description: description
    });

    return result;
}

export const deleteHistory = async (orderId: string, itemIds: Array<string>, historyIds: Array<string>) => {
    const result = await httpPutEmpty(`/orders/${orderId}/item-step-histories/delete`, {
        itemIds: itemIds,
        historyIds: historyIds
    });

    return result;
}

export const putPayment = async (orderId: string, paymentStatus: OrderPaymentStatus, comment?: string | undefined) => {
    const result = await httpPutEmpty(`/orders/${orderId}/payment`, {
        status: paymentStatus,
        comment: comment
    });

    return result;
}